import { APIS, RouteConstants } from '.';
import { CarbonIconType, User, Settings, UserAdmin, UserMultiple } from '@carbon/icons-react';
import silver from '../utils/images/silver.svg';
import satellite from '../utils/images/satellite.svg';
import mapView from '../utils/images/gmapview.svg';
import darkView from '../utils/images/Dark.svg';
import choroplethIcon from '../utils/images/choropleth.svg';
import choroplethInactiveIcon from '../utils/images/ChoroplethInactive.svg';
import markerIcon from '../utils/images/marker.svg';
import markerInactiveIcon from '../utils/images/MarkersInactive.svg';

export interface TableHeaderProps {
    ID: string,
    NAME: string,
    KEYS: { KEY: string, VALUE: string, UOM?: string }[]
}

interface TableProps {
    GEO_INFO_TABLE: TableHeaderProps,
    METRIC_BREAKDOWN_TABLE: TableHeaderProps,
}

export const TABLE_HEADERS: TableProps = {
    GEO_INFO_TABLE: {
        ID: 'geo-info',
        NAME: 'Summary Breakdown',
        KEYS: [
            { KEY: 'geoName', VALUE: 'State' },
            { KEY: 'area', VALUE: 'Area (sq.Km)' },
            { KEY: 'totalEh', VALUE: 'Number of EHs' },
            { KEY: 'tam', VALUE: 'Total Addressable Market', UOM: 'totalEhGtvUOM' },
            { KEY: 'poi', VALUE: 'Points of Interest' }
        ],
    },
    METRIC_BREAKDOWN_TABLE: {
        ID: 'metric-breakdown',
        NAME: 'CTV Breakdown',
        KEYS: [
            { KEY: 'geoName', VALUE: 'State' },
            { KEY: 'entrepreneurialHouseholds', VALUE: 'Entrepreneurial Households' },
            { KEY: 'medianAnnualEhSpend', VALUE: 'Median Annual EH Spend', UOM: 'medianAnnualEhSpendUOM' },
            { KEY: 'medianAnnualEhBorrowing', VALUE: 'Median Annual EH Borrowing', UOM: 'medianAnnualEhBorrowingUOM' },
            { KEY: 'medianAnnualEhIncome', VALUE: 'Median Annual EH Income', UOM: 'medianAnnualEhIncomeUOM' },
            { KEY: 'ehTransactionValue', VALUE: 'EH Transcational Value', UOM: 'ehTransactionValueUOM' }
        ],
    },
}

export interface InviteTableHeader {
    key: string;
    value: string;
}

export const INVITE_TABLE_HEADER: InviteTableHeader[] = [
    {
        key: 'name',
        value: 'Name'
    },
    {
        key: 'role',
        value: 'Role'
    },
    {
        key: 'company_name',
        value: 'Company'
    },
    {
        key: 'company_type_name',
        value: 'Type'
    },
    {
        key: 'last_login',
        value: 'Last Active'
    },
    {
        key: 'status',
        value: 'Status'
    },
];

export const REQUEST_INVITE_TABLE_HEADER: InviteTableHeader[] = [
    {
        key: 'name',
        value: 'Name'
    },
    {
        key: 'status',
        value: 'Status'
    },
];

export interface AccountMenuItem {
    key: number;
    text: string;
    route: string;
    Icon: CarbonIconType;
}

export const ACCOUNT_MENU_ITEMS: AccountMenuItem[] = [
    {
        key: 1,
        text: "My profile",
        route: 'profile',
        Icon: User,
    },
    {
        key: 2,
        text: "Settings",
        route: 'settings',
        Icon: Settings,
    },
    {
        key: 3,
        text: "Users",
        route: 'users',
        Icon: UserMultiple,
    },
];

export const CURRENCY = [
    { key: 'INR', symbol: '₹' },
    { key: 'USD', symbol: '$' },
];

export const GEO_LEVELS = [
    { key: 'country', name: 'Country', list: 'countries' },
    { key: 'state', name: 'States', list: 'states' },
    { key: 'district', name: 'Districts', list: 'districts' },
    { key: 'sub_district', name: 'Sub-districts', list: 'sub_districts' },
    { key: 'village', name: 'Village', list: 'villages' },
];

export const COMPARE_GEO_LEVELS = [
    { key: 'state', name: 'States', list: 'states' },
    { key: 'district', name: 'Districts', list: 'districts' },
    { key: 'sub_district', name: 'Sub-districts', list: 'sub_districts' },
    { key: 'pincode', name: 'Pincode', list: 'pincodes' },
    { key: 'village', name: 'Village', list: 'villages' },
];

export const DATA_CATEGORIES = ['PoI', 'CTV'];

export const GOOGLE_ICON_MAP = new Map([
    [1, 'psychiatry'],
    [2, 'home_health'],
    [3, 'school'],
    [4, 'finance_chip'],
    [5, 'directions_car'],
    [6, 'rss_feed'],
    [7, 'home_work'],
    [8, 'local_mall'],
]);

export const PAGINATION_COUNT_OPTIONS = [
    { key: 25, value: 25 },
    { key: 50, value: 50 },
    { key: 75, value: 75 }
];

export const GEO_VISIBLE_ROUTE = [RouteConstants.root, RouteConstants.dashboard, RouteConstants.something_went_wrong, RouteConstants.no_poi];
export const ICON_HIDDEN_ROUTE = [RouteConstants.help, RouteConstants.profile, RouteConstants.settings, RouteConstants.users];

export interface basicCifProps {
    address: string;
    averageCtv: string;
    averageCtvActualValue: number;
    eh: string;
    ehActualValue: number;
    geo_id: string;
    geo_name: string;
    geoType: string;
    geoLevelNumber: number;
    region: string;
    totalAddressableMarket: string;
    totalAddressableMarketActualValue: number;
    totalHouseholds: string;
    totalHouseholdsActualValue: number;
    totalPopulation: string;
    totalPopulationActualValue: number;
}

export const CATEGORY_ORDER = ['Healthcare', 'Hyperlocal Retail', 'Education', 'Construction', 'Agriculture', 'Mobility', 'Financial Solutions', 'Digital Adoption'];
export const CATEGORY_ORDER_IN_DROPDOWN = ['Healthcare', 'Education', 'Agriculture', 'Financial Solutions', 'Hyperlocal Retail', 'Construction', 'Mobility', 'Digital Adoption'];
export const VIEW_TYPES = [
    { label: 'Sub-District', key: 4 },
    { label: 'PIN Code', key: 5 }
];

export const TABLE_VIEW_TYPES = [
    { label: 'Villages', key: 'region' },
    { label: 'PoI', key: 'poi' },
    { label: 'MBP', key: 'mbp' }
];

export const DISTRICT_BOUNDARY_STYLE = {
    strokeColor: "#333",
    strokeOpacity: 0,
    strokeWeight: 6,
    zIndex: 7,
    icons: [{
        icon: {
            path: "M 0,-0.3 0,0.3",
            strokeOpacity: 1,
            scale: 1.5,
        },
        offset: "0",
        repeat: "5px",
    }],
};

export const DISTRICT_BOUNDARY_BG_STYLE = {
    strokeColor: "#F8F8F8",
    strokeOpacity: 0.6,
    strokeWeight: 4,
    zIndex: 6,
}

export const ROI_RECTANGLE_SPEC = {
    HOVER: { HEIGHT: 0.33, WIDTH: 0.75 },
    POI: { HEIGHT: 0.65, WIDTH: 0.65 },
    MBP: { HEIGHT: 0.35, WIDTH: 0.6 },
}

export const TABLE_STYLE_CONFIG = {
    TOP_REGIONS: {
        PRIMARY_KEY: '',
        PINNED_ROW_INDICES: [0, 1],
    },
    TOP_POIS: {
        PRIMARY_KEY: 'poiInfo.branchName',
        PINNED_ROW_INDICES: [0],
    },
    TOP_MBPS: {
        PRIMARY_KEY: 'companyName',
        PINNED_ROW_INDICES: [0],
    },
    RECOMMENDED_REGIONS: {
        PRIMARY_KEY: 'name',
        PINNED_ROW_INDICES: [0],
    },
    BROWSE_DATA_SOURCES: {
        PRIMARY_KEY: '',
        PINNED_ROW_INDICES: [],
    },
      DATA_PREVIEW :{
           PRIMARY_KEY: 'category',
        PINNED_ROW_INDICES: [],
    }
}

export const SORT_OPTIONS = [
    { dir: "asc", icon: "keyboard_arrow_up" },
    { dir: "desc", icon: "keyboard_arrow_down" },
];

export const POI_TABLE_INFO = 'PoI names and details are user-generated, and may contain special characters or appear in other languages.'

export type GeoType = 'State' | 'Districts' | 'Subdistricts' | 'Pincode' | 'Villages';

export const ADJACENCY_RELATED_APIS = [
    APIS.MAPS.GET_MAP_BOUNDARY_V5,
    APIS.MAPS.GET_MAP_PROPERTIES_V5,
    APIS.MAPS.GET_POI_MAP_COLOR_V3,
    APIS.MAPS.GET_TOP_REGIONS_EXTENDED_V2,
    APIS.MAPS.GET_TOP_POIS,
    APIS.MAPS.GET_BRANDS,
    APIS.CIF.GET_FILTERED_DISTRICTS,
    APIS.CIF.GET_OVERLAY_FILTERS,
    APIS.CIF.GET_SLIDER_DATA,
];

export const RESET_FIELD_ARRAY: any = {
    all: ['country', 'state', 'district', 'sub_district', 'pincode', 'village'],
    village: ['country', 'state', 'district', 'sub_district', 'village'],
    pincode: ['country', 'state', 'district', 'pincode'],
    sub_district: ['country', 'state', 'district', 'sub_district'],
    district: ['country', 'state', 'district'],
    state: ['country', 'state'],
    country: ['country']
};

export const METRIC_DATA = {
    CTV: {
        title: "Core Transaction Value (CTV)",
        key: "CTV",
    },
    EH: {
        title: "Entrepreneurial Households (EH)",
        key: "EH",
    },
    PoI: {
        title: "Points of Interest (PoI)",
        key: "PoI",
    },
};

export const PARENT_METRIC_TYPES = ['EH', 'CTV', 'PoI'];

export const BOUNDARY_TYPES = {
    country: [{ key: 3, title: 'District' }],
    state: [{ key: 3, title: 'District' }],
    district: [{ key: 4, title: 'Sub-district' }, { key: 5, title: 'PIN Code' }],
    sub_district: [{ key: 6, title: 'Village' }],
    pincode: [{ key: 6, title: 'Village' }],
    catchment: [{ key: 7, title: 'Village' }],
}

export type MetricKey = 'EH' | 'CTV' | 'PoI';

export const MAP_STYLES = [
    { key: "roadmap", label: "Silver", image: silver },
    { key: "hybrid", label: "Satellite", image: satellite },
    { key: "map", label: "Standard", image: mapView },
    { key: "dark", label: "Dark", image: darkView } // Assuming 'mapView' is the dark map style image
];

export const DATA_TABS = [
    { key: 'choropleth', activeIcon: choroplethIcon, inactiveIcon: choroplethInactiveIcon, alt: 'Choropleth' },
    { key: 'markers', activeIcon: markerIcon, inactiveIcon: markerInactiveIcon, alt: 'Markers' },
];